import * as React from "react";
import {VeboxModels} from "../types/veboxModels";
import {RestDataLoader} from "../dashboard/widgets/unilever/RestDataLoader";
import {Icon, icons, Tooltip} from "@vebox/etui-core";
import {Display} from "../components/Display";
import {AppTranslations} from "../components/Utils/translations/AppTranslations";
import {ETUI_ICONS_SIZE, VISIBILITY_TREE_ICONS} from "./UserManagement";
import {commercialHierarchyGUID} from "../processes/user_creation/stages/Visibility";
import {UserHierarchy, UserHierarchyForRoles} from "./contexts/UserVisibilityHierarchiesContext";
import {buildFilterListRequest, HIERARCHY_TYPES_GUID} from "./contexts/UserListContext";
import {commercialHierarchyTreeToList, last} from "../processes/user_creation/UserCreation";
import {VeboxTreeView} from "../components/tree_view/VeboxTreeView";

declare var AppTranslations: AppTranslations

export interface HierarchiesSection {
    selectedHierarchies: string[] | string,
    selectedCommercialHierarchies: any[],
}

export type CommercialHierarchyNode = {
    value: string,
    checked: boolean,
    indeterminate: boolean,
    children: (string | CommercialHierarchyNode)[],
}


/**
 * Get the hierarchies
 */
export function getHierarchies() {
    return new Promise<VeboxModels.HierarchiesTree[]>((res, rej) => {
        RestDataLoader.loadData(
            "users/current/hierarchies/tree",
            {},
            data => res(data.list),
            rej
        )
    });
}

/**
 * Check if the hierarchy label include the search word
 * @param word
 */
export const hierarchyIncludesWord = (word: string) => (hierarchy: VeboxModels.HierarchiesTree) => {
    return hierarchy.label.toLowerCase().includes(word.toLowerCase()) || (hierarchy.children ?? []).filter(
        hierarchyIncludesWord(word)).length > 0
}

/**
 * Returns all the selected nodes in the tree
 * @param selectionTree
 */
/**
 * Returns all the selected nodes in the tree
 * @param selectionTree
 */
export function getAllSelectedNodes(selectionTree: (string | {
    children: any
})[]): string[] {
    return selectionTree.reduce<string[]>((list, element) => {
        if (typeof element === "string") {
            return [...list, element]
        }
        return [...list, ...getAllSelectedNodes(element.children)]

    }, [])
}

/**
 * Returns true if the tree has children selected
 * @param tree
 * @param selected
 */
export function isChildSelected(tree: VeboxModels.HierarchiesTree, selected: string[]): boolean {
    return tree.children.reduce<boolean>((result, subTree) => {
        if (result) {
            return result
        }
        return selected.includes(subTree.hierarchyGuid) || isChildSelected(subTree, selected)
    }, false)
}

/**
 * Returns true if the tree has all the children selected
 * @param tree
 * @param selected
 */
export function allChildrenSelected(tree: VeboxModels.HierarchiesTree, selected: string[]): boolean {
    return tree.children.reduce<boolean>((result, subTree) => {
        if (!result) {
            return false
        }
        return selected.includes(subTree.hierarchyGuid) || allChildrenSelected(subTree, selected)
    }, true)
}

/**
 * Check if the selectedCommercialHierarchyNodes contains the node in the hierarchy
 * @param selectedCommercialHierarchyNodes
 * @param hierarchy
 */
export const isPlacedHereInCommercialHierarchy = (selectedCommercialHierarchyNodes: string[],
                                                  hierarchy: VeboxModels.HierarchiesTree) => {
    return selectedCommercialHierarchyNodes.includes(hierarchy.hierarchyGuid)
}


/**
 * Gets only the valid children of a component,
 * and ignores any nullish or falsy child.
 *
 * @param children the children
 */
export function getValidChildren(children: React.ReactNode) {
    return React.Children.toArray(children).filter((child) =>
        React.isValidElement(child),
    ) as React.ReactElement[]
}

interface IconInfoLegendProps {
    showWarningIcon: boolean
}

/**
 * Show a legend to explain the icons for the visibility
 * @param showWarningIcon
 * @constructor
 */
export const IconInfoLegend: React.FC<IconInfoLegendProps> = ({showWarningIcon}) => {
    const {placedCompany, ceasedCompany, visibleCompany} = AppTranslations.usersManagement

    const legend = (): JSX.Element => {
        return <div className={"visibility-legend"}>
            <ul>
                <li className={"visibility-legend-item"}>
                    <VisibilityIcon iconName={VISIBILITY_TREE_ICONS.placed}/>
                    <span className={"visibility-legend-text"}>{placedCompany}</span>
                </li>
                <hr className={"white-bottom-border"}/>
                <li className={"visibility-legend-item"}>
                    <VisibilityIcon iconName={VISIBILITY_TREE_ICONS.visibility}/>
                    <span className={"visibility-legend-text"}>{visibleCompany}</span>
                </li>
                <Display isVisible={showWarningIcon}>
                    <hr className={"white-bottom-border"}/>
                    <li className={"visibility-legend-item"}>
                        <VisibilityIcon iconName={VISIBILITY_TREE_ICONS.ceasedCompany}/>
                        <span className={"visibility-legend-text"}>{ceasedCompany}</span>
                    </li>
                </Display>
            </ul>
        </div>
    }

    return (
        <Tooltip
            message={legend}
            placement={"left"}
        >
            <Icon glyph={icons.INFO_CIRCLE} size={ETUI_ICONS_SIZE}/>
        </Tooltip>
    )
}

interface VisibilityIconProps {
    iconName: VISIBILITY_TREE_ICONS
}

/**
 * Return the correct icon from the company
 * @param iconName the name of the icon
 */
export const VisibilityIcon: React.FC<VisibilityIconProps> = ({iconName}): JSX.Element => {
    switch (iconName) {
        case VISIBILITY_TREE_ICONS.visibility:
            return <span className="material-icons visibility-legend-icon icon">{VISIBILITY_TREE_ICONS.visibility}</span>
        case VISIBILITY_TREE_ICONS.ceasedCompany:
            return <span className="material-icons visibility-legend-icon icon">{VISIBILITY_TREE_ICONS.ceasedCompany}</span>
        case VISIBILITY_TREE_ICONS.placed:
            return <span className="material-icons visibility-legend-icon icon">{VISIBILITY_TREE_ICONS.placed}</span>
        default:
            return undefined
    }
};

/**
 * Returns the list of hierarchy nodes from the commercial hierarchies tree
 * @param guidList
 * @param commercialHierarchy
 * @constructor
 */
export function CommercialHierarchyTreeToList(guidList: string[],
                                              commercialHierarchy: VeboxModels.HierarchiesTree): (string | CommercialHierarchyNode)[] {
    const hierarchy: (string | CommercialHierarchyNode)[] = createCheckBoxTreeBranch(guidList, commercialHierarchy);

    let result: (string | CommercialHierarchyNode)[] = [];

    if (hierarchy.length && typeof hierarchy[0] !== "string") {
        hierarchy[0].children.forEach((tmpNode) => {
            result = [...result, tmpNode]
        })
    }

    return result;
}

/**
 * Returns the list of guids from the HierarchiesTree
 * @param guid
 * @param hierarchiesTree
 * @param hierarchyGuidToFollow
 * @constructor
 */
export function HierarchyTreeToList(guid: string, hierarchiesTree: VeboxModels.HierarchiesTree[],
                                    hierarchyGuidToFollow: string): string[] {
    const treeBranch: VeboxModels.HierarchiesTree[] = hierarchiesTree.filter(
        (tree) => tree.hierarchyGuid === hierarchyGuidToFollow)
    return treeBranch.length ? createTreeBranch(guid, treeBranch[0]) : [];
}

/**
 * Returns true if the node is included in the hierarchy
 * @param nodeGuid
 */
export const hierarchyIncludesPlacedNode = (nodeGuid: string) => (hierarchy: VeboxModels.HierarchiesTree) => {
    return hierarchy.hierarchyGuid.includes(nodeGuid) || (hierarchy.children ?? []).filter(
        hierarchyIncludesPlacedNode(nodeGuid)).length > 0
}

/**
 * Returns true if at least one node is included in the hierarchy
 * @param nodeGuids
 */
export const hierarchyIncludesPlacedNodes = (nodeGuids: string[]) => (hierarchy: VeboxModels.HierarchiesTree) => {

    return nodeGuids.some(guid => guid === hierarchy.hierarchyGuid) || (hierarchy.children ?? []).filter(
        hierarchyIncludesPlacedNodes(nodeGuids)).length > 0
}

export function useValue(v) {
    return typeof v === 'object' ? v.value : v;
}


/**
 * Returns true if the commercial hierarchy has to be expanded
 * @param commercialHierarchy
 * @param hierarchiesSelected
 * @param searchWord
 */
export const expandCommercialHierarchy = (commercialHierarchy: VeboxModels.HierarchiesTree,
                                          hierarchiesSelected: HierarchiesSection, searchWord: string): boolean => {

    const commercialHierarchyIncludesSearchWord: boolean = searchWord !== "" &&
        (commercialHierarchy.label.includes(searchWord) ||
            (commercialHierarchy.children ?? [])
                .filter(hierarchyIncludesWord(searchWord)).length > 0)

    const includesCommercialHierarchy = (
        Array.isArray(hierarchiesSelected?.selectedHierarchies)
            ? hierarchiesSelected?.selectedHierarchies
            : [])
        .find((elem) => elem === commercialHierarchyGUID) !== undefined

    return commercialHierarchyIncludesSearchWord || includesCommercialHierarchy
}

/**
 * Returns the tree branch of the commercial tree
 * @param guidList
 * @param commercialHierarchy
 */
export const createCheckBoxTreeBranch = (guidList: string[],
                                         commercialHierarchy: VeboxModels.HierarchiesTree): (string | CommercialHierarchyNode)[] => {


    if (guidList.find((guid) => guid === commercialHierarchy.hierarchyGuid) !== undefined) {
        return [commercialHierarchy.hierarchyGuid];
    } else if (commercialHierarchy?.children && commercialHierarchy?.children.length > 0) {
        let newNode: CommercialHierarchyNode | string;
        let recursiveResults: (string | CommercialHierarchyNode)[] = [];

        commercialHierarchy.children.forEach((node) => {
            recursiveResults = [...recursiveResults, ...createCheckBoxTreeBranch(guidList, node)]
        });

        if (recursiveResults.length > 0) {
            newNode = {
                value: commercialHierarchy.hierarchyGuid,
                indeterminate: true,
                checked: false,
                children: recursiveResults
            }
            return [newNode];
        }
    }
    return [];
}

/**
 * Returns the tree branch in guid list format
 * @param guidToSearch
 * @param hierarchiesTree
 */
export const createTreeBranch = (guidToSearch: string, hierarchiesTree: VeboxModels.HierarchiesTree): string[] => {
    if (hierarchiesTree.hierarchyGuid === guidToSearch) {
        return [hierarchiesTree.hierarchyGuid]
    } else {
        let hierarchy: string[] = [];
        hierarchiesTree.children.forEach((child) => {
            hierarchy = [...hierarchy, ...createTreeBranch(guidToSearch, child)]
        })
        return hierarchy.length > 0 ? [hierarchiesTree.hierarchyGuid, ...hierarchy] : [];
    }
}

/**
 * Returns the guid of the hierarchy type
 * @param selectedHierarchies
 */
const getHierarchyTypeGuid = (selectedHierarchies: HierarchiesSection): string => {

    if (typeof selectedHierarchies.selectedHierarchies !== "string") {
        return HIERARCHY_TYPES_GUID.find(
            (hierarchyTypeGuid: string) => (selectedHierarchies.selectedHierarchies as string[])
                .find((selectedHierarchyGuid) => selectedHierarchyGuid === hierarchyTypeGuid)
        )
    }
    return "";
}

/**
 * Returns the list of guid selected
 * @param selectedHierarchies
 */
const getHierarchyNodesGuids = (selectedHierarchies: HierarchiesSection): string [] => {
    if (typeof selectedHierarchies.selectedHierarchies === "string") {
        return []
    } else if (selectedHierarchies.selectedCommercialHierarchies.length > 0) {
        return getAllSelectedNodes(selectedHierarchies?.selectedCommercialHierarchies)
    }

    return [last(selectedHierarchies.selectedHierarchies)]
}

/**
 * Maps the given HierarchiesSection into a userHierarchyForRoles
 * @param selectedHierarchies
 */
export const mapHierarchiesSectionIntoUserHierarchy = (selectedHierarchies: HierarchiesSection): UserHierarchyForRoles => {
    return {
        hierarchyTypeGuid: getHierarchyTypeGuid(selectedHierarchies),
        hierarchyNodesGuids: getHierarchyNodesGuids(selectedHierarchies)
    }
}

/**
 * Maps the given hierarchy into a userHierarchyForRoles
 * @param hierarchy
 */
export const mapUserHierarchy = (hierarchy: UserHierarchy): UserHierarchyForRoles | {} => {
    return hierarchy && !hierarchy?.empty
        ? {
            hierarchyTypeGuid: hierarchy.hierarchyTypeGuid,
            hierarchyNodesGuids: hierarchy.hierarchyNodesGuids[0]
        }
        : {}
}

/**
 * Maps the given hierarchy into string format
 * @param hierarchy
 * @param userCompanyGuid
 */
export const mapUserHierarchyForRolesIntoString = (hierarchy: UserHierarchyForRoles,
                                                   userCompanyGuid?: string): string => {

    const isEmptyHierarchy: boolean = hierarchy?.hierarchyNodesGuids.length === 0 && hierarchy?.hierarchyTypeGuid === ""
    const hierarchyTypeGuid = hierarchy?.hierarchyTypeGuid;
    const hierarchyNodesGuids = !isEmptyHierarchy ? hierarchy.hierarchyNodesGuids : [userCompanyGuid]

    return `${buildFilterListRequest(
        [["hierarchyTypeGuid", [hierarchyTypeGuid] ?? []]])}&${buildFilterListRequest(
        [["hierarchyNodesGuids", hierarchyNodesGuids ?? []]])}`
}


export const hierarchyToFilteredVeboxTreeItem = (word: string,
                                                 selectedCompanyGuid: string,
                                                 isFullVisibility: boolean,
                                                 hasParentSelected: boolean,
                                                 ceasedCompanyGuid?: string) => (hierarchy: VeboxModels.HierarchiesTree) => {

    const isPlacedHere = selectedCompanyGuid === hierarchy.hierarchyGuid;
    const isCeased = ceasedCompanyGuid === hierarchy.hierarchyGuid;
    const expand = !!selectedCompanyGuid && (hierarchy.children ?? []).filter(
        hierarchyIncludesPlacedNode(selectedCompanyGuid)).length > 0

    if (!hierarchy.label.toLowerCase().includes(word.toLowerCase()) && (hierarchy.children ?? []).filter(
        hierarchyIncludesWord(word)).length === 0) {
        return undefined;
    }

    const label = (checked) => (
        <>
            <Display isVisible={isPlacedHere}>
                <VisibilityIcon iconName={VISIBILITY_TREE_ICONS.placed}/>
            </Display>
            <Display isVisible={isCeased}>
                <VisibilityIcon iconName={VISIBILITY_TREE_ICONS.ceasedCompany}/>
            </Display>
            <Display isVisible={isFullVisibility || hasParentSelected}>
                <VisibilityIcon iconName={VISIBILITY_TREE_ICONS.visibility}/>
            </Display>
            <div className={`font-weight-400 hierarchy-leaf ${checked ? "bold" : ""}`}>{hierarchy.label}</div>
        </>
    );

    const doesIncludeSearchWord = (hierarchy.label.toLowerCase().includes(word.toLowerCase())
            || (hierarchy.children ?? []).filter(hierarchyIncludesWord(word)).length > 0)
        && word !== "";

    return (
        <VeboxTreeView.Radio
            key={hierarchy.hierarchyGuid}
            value={hierarchy.hierarchyGuid}
            expanded={doesIncludeSearchWord || isPlacedHere || expand}
            label={label}
            disabled={isCeased}
        >
            {(hierarchy.children ?? [])
                .map(hierarchyToFilteredVeboxTreeItem(word, selectedCompanyGuid, isFullVisibility,
                    hasParentSelected || isPlacedHere, ceasedCompanyGuid))}
        </VeboxTreeView.Radio>
    );
}

export function hierarchyToFilteredVeboxCheckboxTree(word: string, selectedCommercialHierarchyNodes: string[],
                                                     hasParentBeenSelected: boolean, isAllCompanies: boolean) {
    return (hierarchy: VeboxModels.HierarchiesTree) => {

        if (!hierarchy.label.toLowerCase().includes(word.toLowerCase()) && (hierarchy.children ?? []).filter(
            hierarchyIncludesWord(word)).length === 0) {
            return undefined;
        }

        const doesIncludeSearchWord = (hierarchy.label.toLowerCase().includes(word.toLowerCase())
                || (hierarchy.children ?? []).filter(hierarchyIncludesWord(word)).length > 0)
            && word !== "";

        const isBold = isChildSelected(hierarchy, selectedCommercialHierarchyNodes)

        const isPlacedHere = isPlacedHereInCommercialHierarchy(selectedCommercialHierarchyNodes, hierarchy)
        const expand = !!selectedCommercialHierarchyNodes && (hierarchy.children ?? []).filter(
            hierarchyIncludesPlacedNodes(selectedCommercialHierarchyNodes)).length > 0

        return (
            <VeboxTreeView.CheckboxWithoutIndeterminate
                key={hierarchy.hierarchyGuid}
                value={hierarchy.hierarchyGuid}
                expanded={doesIncludeSearchWord || isPlacedHere || expand}
                label={
                    <>
                        <Display isVisible={isPlacedHere}>
                            <VisibilityIcon iconName={VISIBILITY_TREE_ICONS.placed}/>
                        </Display>
                        <Display isVisible={hasParentBeenSelected || isAllCompanies}>
                            <VisibilityIcon iconName={VISIBILITY_TREE_ICONS.visibility}/>
                        </Display>
                        <div className={`font-weight-400 hierarchy-leaf ${isBold ? "bold" : ""}`}>{hierarchy.label}</div>
                    </>
                }
            >
                {(hierarchy.children ?? []).map(
                    hierarchyToFilteredVeboxCheckboxTree(word, selectedCommercialHierarchyNodes,
                        isPlacedHere || hasParentBeenSelected, isAllCompanies))}
            </VeboxTreeView.CheckboxWithoutIndeterminate>
        );
    }
}

export const haveSameVisibility = (defaultHierarchy: HierarchiesSection,
                                   selectedHierarchy: HierarchiesSection): boolean => {

    if (Array.isArray(defaultHierarchy.selectedHierarchies) && Array.isArray(selectedHierarchy.selectedHierarchies)) {
        if (defaultHierarchy.selectedHierarchies.length === selectedHierarchy.selectedHierarchies.length) {
            for (const sH of selectedHierarchy.selectedHierarchies) {
                if (defaultHierarchy.selectedHierarchies.find((dH) => dH === sH) === undefined) {
                    return false;
                }
            }

            const userHierarchyToCheck: string[] = commercialHierarchyTreeToList(
                selectedHierarchy.selectedCommercialHierarchies);
            const defaultHierarchyConverted: string[] = commercialHierarchyTreeToList(
                defaultHierarchy.selectedCommercialHierarchies);

            if (userHierarchyToCheck.length === defaultHierarchyConverted.length) {
                for (const htc of userHierarchyToCheck) {
                    if (defaultHierarchyConverted.find((dhc) => dhc === htc) === undefined) {
                        return false;
                    }
                }
                return true;
            }
        }
        return false;
    }

    if (!Array.isArray(defaultHierarchy.selectedHierarchies)
        && !Array.isArray(selectedHierarchy.selectedHierarchies)) {
        return defaultHierarchy.selectedHierarchies === selectedHierarchy.selectedHierarchies;
    }
    return false;
}


export const changeSelectedHierarchy = (v, hierarchiesSelected: HierarchiesSection) => {
    return {
        ...hierarchiesSelected,
        selectedHierarchies: v,
        selectedCommercialHierarchies: [],
    };
};

export const changeSelectedCommercialHierarchies = (v, hierarchiesSelected: HierarchiesSection) => {
    return {
        ...hierarchiesSelected,
        selectedHierarchies: (v ?? []).length > 0 ? [commercialHierarchyGUID] : [],
        selectedCommercialHierarchies: v,
    };
};


export const checkIfIsSubmittable = (hierarchiesSelected: HierarchiesSection): boolean => {
    if (Array.isArray(hierarchiesSelected.selectedHierarchies)) {
        if (hierarchiesSelected.selectedHierarchies[0] === commercialHierarchyGUID) {
            return hierarchiesSelected.selectedHierarchies.length === 1 && hierarchiesSelected.selectedCommercialHierarchies.length > 0;
        } else {
            return hierarchiesSelected.selectedHierarchies.length > 0 && hierarchiesSelected.selectedCommercialHierarchies.length === 0;
        }
    } else {
        return (Array.isArray(hierarchiesSelected.selectedHierarchies)
            ? hierarchiesSelected.selectedHierarchies[0]
            : hierarchiesSelected.selectedHierarchies) === "all_companies";
    }
}

export const addCeasedCompanyInHierarchy = (currentUserHierarchies: UserHierarchy, ceasedCompany: VeboxModels.Company,
                                            hierarchiesTree: VeboxModels.HierarchiesTree[]): VeboxModels.HierarchiesTree[] => {


    return hierarchiesTree.map((h) => {
        if (h.hierarchyGuid === currentUserHierarchies.hierarchyTypeGuid) {
            const ceasedCompanyInHierarchy: VeboxModels.HierarchiesTree = {
                label: ceasedCompany.name,
                children: [],
                hierarchyGuid: ceasedCompany.guid
            }
            return {
                hierarchyGuid: h.hierarchyGuid,
                label: h.label,
                children: [ceasedCompanyInHierarchy, ...h.children]
            }
        } else {
            return h
        }
    });
}


interface EditableHierarchyProps {
    changeEditingStatus: () => void;
    isEditingMode: boolean;
}

/**
 * Manage the icon for editing the user hierarchy
 * @param changeEditingStatus triggers the editingMode change status
 * @param isEditingMode
 */
export const EditableHierarchy: React.FC<EditableHierarchyProps> = ({changeEditingStatus, isEditingMode}) => {


    const EditHierarchyIcon = (): JSX.Element => {
        return <>
            <Display isVisible={!isEditingMode}>
                <span
                    className="material-icons clickable edit-icon edit-user-visibility-icon"
                    onClick={changeEditingStatus}
                >edit</span>
            </Display>
            <Display isVisible={isEditingMode}>
                <span
                    className="clickable"
                    onClick={changeEditingStatus}
                ><Icon glyph={icons.CLOSE} size={ETUI_ICONS_SIZE}/></span>
            </Display>
        </>
    }

    return (
        <Tooltip
            message={isEditingMode ? AppTranslations.usersManagement.exitEditingMode : AppTranslations.usersManagement.editUserHierarchy}
            placement={"left"}
        >
            <EditHierarchyIcon/>
        </Tooltip>
    )
}